import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Package, Sun, Battery, Zap, Grid, List,
  ChevronDown, X, ShoppingCart, Eye, Phone, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  panel_solar: Sun,
  inversor: Zap,
  bateria: Battery,
  estructura: Grid,
  cableado: Zap,
  proteccion: Zap,
  monitoreo: Eye,
  accesorios: Package,
  electrodomesticos: Package,
  iluminacion: Sun,
  moto_electrica: Package,
  estacion_carga: Zap
};

const CatalogoPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, searchTerm]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/api/products/public/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/products/public/catalog?`;
      if (selectedCategory) url += `category=${selectedCategory}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleWhatsAppContact = (product) => {
    const message = `Hola Zenex! Me interesa el producto: ${product.name} (${product.sku}). ¿Me pueden dar más información?`;
    window.open(`https://wa.me/595991344956?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-4">
              <Package className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Catálogo de Productos</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Productos Solares <span className="text-[#90EE90]">Premium</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-8">
              Equipos de las mejores marcas mundiales con garantía y soporte técnico local
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-zinc-400 focus:outline-none focus:border-[#90EE90] transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8">
            {/* Category Filter - Mobile Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-lg text-zinc-700"
            >
              <Filter className="w-4 h-4" />
              Filtrar por categoría
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {/* Category Pills - Desktop */}
            <div className="hidden sm:flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedCategory
                    ? 'bg-[#90EE90] text-zinc-900'
                    : 'bg-white border border-zinc-200 text-zinc-600 hover:border-[#90EE90]'
                }`}
              >
                Todos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat.value
                      ? 'bg-[#90EE90] text-zinc-900'
                      : 'bg-white border border-zinc-200 text-zinc-600 hover:border-[#90EE90]'
                  }`}
                >
                  {cat.label} ({cat.count})
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-zinc-100' : ''}`}
              >
                <Grid className="w-4 h-4 text-zinc-600" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-zinc-100' : ''}`}
              >
                <List className="w-4 h-4 text-zinc-600" />
              </button>
            </div>
          </div>

          {/* Mobile Category Filter Dropdown */}
          {showFilters && (
            <div className="sm:hidden bg-white border border-zinc-200 rounded-xl p-4 mb-6 space-y-2">
              <button
                onClick={() => { setSelectedCategory(null); setShowFilters(false); }}
                className={`w-full text-left px-4 py-2 rounded-lg text-sm ${
                  !selectedCategory ? 'bg-[#90EE90]/20 text-[#14532D] font-medium' : 'text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                Todos los productos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => { setSelectedCategory(cat.value); setShowFilters(false); }}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm ${
                    selectedCategory === cat.value ? 'bg-[#90EE90]/20 text-[#14532D] font-medium' : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  {cat.label} ({cat.count})
                </button>
              ))}
            </div>
          )}

          {/* Results Count */}
          <p className="text-sm text-zinc-500 mb-6">
            {products.length} producto{products.length !== 1 ? 's' : ''} encontrado{products.length !== 1 ? 's' : ''}
          </p>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#90EE90] animate-spin" />
              <span className="ml-3 text-zinc-500">Cargando productos...</span>
            </div>
          ) : products.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-zinc-900 mb-2">No se encontraron productos</h3>
              <p className="text-zinc-500 mb-6">
                {searchTerm 
                  ? 'Probá con otros términos de búsqueda' 
                  : 'Pronto agregaremos más productos a esta categoría'}
              </p>
              <button
                onClick={() => { setSearchTerm(''); setSelectedCategory(null); }}
                className="px-6 py-3 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors"
              >
                Ver todos los productos
              </button>
            </div>
          ) : (
            /* Products Grid/List */
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6' 
              : 'space-y-4'
            }>
              {products.map((product) => {
                const CategoryIcon = categoryIcons[product.category] || Package;
                
                return viewMode === 'grid' ? (
                  /* Grid Card */
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                    data-testid={`product-card-${product.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 bg-zinc-100 overflow-hidden">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CategoryIcon className="w-16 h-16 text-zinc-300" />
                        </div>
                      )}
                      
                      {product.is_featured && (
                        <div className="absolute top-3 left-3 bg-[#90EE90] text-zinc-900 px-2 py-1 rounded text-xs font-bold">
                          Destacado
                        </div>
                      )}
                      
                      {product.discount_percent > 0 && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                          -{product.discount_percent}%
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-zinc-500 uppercase tracking-wider">{product.brand}</span>
                        {product.stock_quantity > 0 ? (
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">En stock</span>
                        ) : (
                          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Consultar</span>
                        )}
                      </div>
                      
                      <h3 className="font-bold text-zinc-900 mb-1 line-clamp-2">{product.name}</h3>
                      <p className="text-xs text-zinc-500 mb-3">SKU: {product.sku}</p>
                      
                      {/* Specs */}
                      {product.power_watts && (
                        <p className="text-sm text-zinc-600 mb-3">
                          <Zap className="w-4 h-4 inline mr-1 text-[#90EE90]" />
                          {product.power_watts}W
                        </p>
                      )}
                      
                      {/* Price */}
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-2xl font-bold text-zinc-900">{formatPrice(product.price_usd)}</p>
                          {product.warranty_years > 0 && (
                            <p className="text-xs text-zinc-500">{product.warranty_years} años garantía</p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-2">
                        <button
                          onClick={() => handleWhatsAppContact(product)}
                          className="flex-1 bg-[#90EE90] text-zinc-900 py-2 rounded-lg font-bold text-sm hover:bg-[#7CDF7C] transition-colors flex items-center justify-center gap-1"
                        >
                          <Phone className="w-4 h-4" />
                          Consultar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* List Card */
                  <div
                    key={product.id}
                    className="bg-white rounded-xl border border-zinc-200 p-4 flex gap-4 hover:shadow-lg transition-all"
                  >
                    {/* Image */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 bg-zinc-100 rounded-lg overflow-hidden flex-shrink-0">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CategoryIcon className="w-8 h-8 text-zinc-300" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-xs text-zinc-500 uppercase">{product.brand}</p>
                          <h3 className="font-bold text-zinc-900 truncate">{product.name}</h3>
                          <p className="text-xs text-zinc-500">SKU: {product.sku}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xl font-bold text-zinc-900">{formatPrice(product.price_usd)}</p>
                          {product.stock_quantity > 0 ? (
                            <span className="text-xs text-green-600">En stock</span>
                          ) : (
                            <span className="text-xs text-orange-600">Consultar</span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{product.short_description || product.description}</p>
                      
                      <button
                        onClick={() => handleWhatsAppContact(product)}
                        className="mt-3 bg-[#90EE90] text-zinc-900 px-4 py-2 rounded-lg font-bold text-sm hover:bg-[#7CDF7C] transition-colors inline-flex items-center gap-2"
                      >
                        <Phone className="w-4 h-4" />
                        Consultar precio
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿No encontrás lo que buscás?
          </h2>
          <p className="text-zinc-400 mb-8">
            Contamos con acceso a las mejores marcas del mercado. Consultanos por cualquier equipo.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Busco%20un%20producto%20que%20no%20encuentro%20en%20el%20catálogo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors"
          >
            <Phone className="w-5 h-5" />
            Consultar por WhatsApp
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default CatalogoPage;
